import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { NavigateFunction } from 'react-router-dom';

import { RequestState } from '../../core/enums/requestState';
import { RootState } from '../../core/store/store';
import { MenuItems } from '../enums/menu-items.enum';
import { Permissions } from '../enums/permissions.enum';
import { UserRole } from '../enums/user-roles.enum';
import { FirebaseLoginResponseDTO } from '../http/authAPI';
import { LoginBackendResponseDTO, SysConf } from '../http/dto/loginBackendResponseDto';
import { LoginErrors } from '../http/dto/loginErrors';
import { AuthorizationRecord } from '../types/authorization-record.type';

export interface AuthState {
  requestStates: {
    auth: RequestState;
    logout: RequestState;
  };
  displayName: string;
  email: string;
  expiresIn: string;
  idToken: string;
  localId: string;
  refreshToken: string;
  errors?: LoginErrors;
  userData: {
    fullName: string;
    personId?: number;
  };
  rights: string[];
  loginRemainingTime?: number;
  systemConfigs: SysConf[];
  allowedMenuItems: string[];
  sessionId?: string;
  isLoggedIn: boolean;
  isLogoutDialogOpen: boolean;
  userRole: UserRole;
  authorizationRecord: AuthorizationRecord | null;
}

const getAuthStateFromLocalStorage = () => {
  const authStateFromLocalStorage = localStorage.getItem('authState');
  if (!authStateFromLocalStorage) {
    return { requestState: RequestState.IDLE };
  }

  const parsedState = JSON.parse(authStateFromLocalStorage);

  return { ...parsedState, requestState: RequestState.RESOLVED };
};

const emptyAuthState: AuthState = {
  requestStates: {
    auth: RequestState.IDLE,
    logout: RequestState.IDLE,
  },
  displayName: '',
  email: '',
  expiresIn: '',
  idToken: '',
  localId: '',
  refreshToken: '',
  userData: {
    fullName: '',
  },
  rights: [],
  systemConfigs: [],
  allowedMenuItems: [],
  isLoggedIn: false,
  isLogoutDialogOpen: false,
  userRole: UserRole.UNKNOWN,
  authorizationRecord: null,
};

const initialAuthState: AuthState = {
  ...emptyAuthState,
  ...getAuthStateFromLocalStorage(),
};

const authSlice = createSlice({
  name: 'auth',
  initialState: initialAuthState,
  reducers: {
    logInWithEmailAndPassword(state: AuthState, _2: PayloadAction<{ email: string; password: string; navigate: NavigateFunction }>) {
      state.requestStates.auth = RequestState.PENDING;
      state.isLoggedIn = false;
    },

    logInWithEmailAndPasswordSuccess(
      state: AuthState,
      action: PayloadAction<{ firebaseLoginResponseDTO: FirebaseLoginResponseDTO; navigate: NavigateFunction }>
    ) {
      return {
        ...state,
        ...action.payload.firebaseLoginResponseDTO,
        isLoggedIn: false,
      };
    },

    logInWithEmailAndPasswordError(state: AuthState, action: PayloadAction<{ errors: LoginErrors }>) {
      state.requestStates.auth = RequestState.REJECTED;
      state.errors = action.payload.errors;
      state.isLoggedIn = false;
    },

    logInWithLocalId(state: AuthState, _: PayloadAction<{ email: string; password: string; navigate: NavigateFunction }>) {
      state.requestStates.auth = RequestState.PENDING;
      state.isLoggedIn = false;
    },

    logInWithLocalIdSuccess(
      state: AuthState,
      action: PayloadAction<{ loginBackendResponseDTO: LoginBackendResponseDTO; navigate: NavigateFunction }>
    ) {
      state.requestStates.auth = RequestState.RESOLVED;
      state.userData = {
        personId: action.payload.loginBackendResponseDTO.personID,
        fullName: action.payload.loginBackendResponseDTO.fullName,
      };
      state.rights = action.payload.loginBackendResponseDTO.rights;
      state.loginRemainingTime = action.payload.loginBackendResponseDTO.loginRemainTime;
      state.systemConfigs = action.payload.loginBackendResponseDTO.sysConfs;
      state.allowedMenuItems = action.payload.loginBackendResponseDTO.menus;
      state.sessionId = action.payload.loginBackendResponseDTO.sessionID;
      state.userRole = action.payload.loginBackendResponseDTO.roleName;
      state.authorizationRecord = action.payload.loginBackendResponseDTO.authorization;

      state.isLoggedIn = true;
    },

    logInWithLocalIdError(state: AuthState, action: PayloadAction<{ error: string }>) {
      return {
        ...initialAuthState,
        requestStates: {
          ...state.requestStates,
          auth: RequestState.REJECTED,
        },
        errors: { otherError: action.payload.error },
        isLoggedIn: false,
      };
    },

    openLogoutDialog(state: AuthState) {
      return {
        ...state,
        isLogoutDialogOpen: true,
      };
    },

    closeLogoutDialog(state: AuthState) {
      return {
        ...state,
        isLogoutDialogOpen: false,
      };
    },

    logout(state: AuthState, _: PayloadAction<{ navigate: NavigateFunction; isUnauthorized?: boolean }>) {
      state.requestStates.logout = RequestState.PENDING;
    },

    logoutSuccess() {
      return {
        ...emptyAuthState,
      };
    },

    logoutError(state: AuthState) {
      state.requestStates.logout = RequestState.REJECTED;
    },

    loginToFirebaseByOAuth(state: AuthState) {
      return {
        ...state,
        requestStates: {
          ...state.requestStates,
          auth: RequestState.PENDING,
        },
      };
    },

    loginToFirebaseByOAuthError(state: AuthState, action: PayloadAction<{ errorMessage: string }>) {
      return {
        ...state,
        requestState: RequestState.REJECTED,
        errors: { otherError: action.payload.errorMessage, emailError: undefined, passwordError: undefined },
      };
    },

    loginToFirebaseByOAuthSuccess(state: AuthState, payload: PayloadAction<{ idToken: string; navigate: NavigateFunction }>) {
      return {
        ...state,
        idToken: payload.payload.idToken,
        requestStates: {
          ...state.requestStates,
          auth: RequestState.PENDING,
        },
      };
    },
  },
});

export const authActions = authSlice.actions;
export const authReducers = authSlice.reducer;
export const selectAuthState = (state: RootState) => state.auth;
export const selectIsAuthLoading = (state: RootState) => selectAuthState(state).requestStates.auth === RequestState.PENDING;
export const selectIsLogoutDialogOpen = (state: RootState) => selectAuthState(state).isLogoutDialogOpen;
export const selectIsUserLoggedIn = (state: RootState) => selectAuthState(state).isLoggedIn;
export const selectAuthorizationRecord = (state: RootState) => selectAuthState(state).authorizationRecord;
export const selectUserRole = (state: RootState) => selectAuthState(state).userRole;
export const selectUserName = (state: RootState) => selectAuthState(state).userData.fullName;
export const selectUserEmail = (state: RootState) => selectAuthState(state).email;
export const selectIsLogoutLoading = (state: RootState) => selectAuthState(state).requestStates.logout === RequestState.PENDING;

const unselectableMenus = [MenuItems.DASHBOARD, MenuItems.GALLERY_MAIN_MENU];

export const selectSelectableMenuOptionsForUser = () =>
  createSelector([selectAuthorizationRecord], (authorizationRecord) => {
    if (authorizationRecord) {
      return (Object.keys(authorizationRecord) as Array<keyof typeof authorizationRecord>)
        .filter((key) => authorizationRecord[key]?.includes(Permissions.READ) || authorizationRecord[key]?.includes(Permissions.WRITE))
        .filter((menu) => !unselectableMenus.includes(menu));
    }
    return [];
  });

export const selectHasUserWriteAccess = (menuName: MenuItems) =>
  createSelector([selectAuthorizationRecord], (authorizationRecord) => {
    if (!authorizationRecord || !authorizationRecord[menuName]) {
      return false;
    }

    if (authorizationRecord[menuName]?.includes(Permissions.WRITE)) {
      return true;
    }

    return false;
  });
